import styled from "@emotion/styled";
import { fontPlayfairDisplay } from "@product/scmp-sdk";

import { StyleAuthorCardContainer } from "~/components/article/article-author-details/author-card/styles";
import { ArticleHeaderAdSlot } from "~/components/article/article-header-ad-slot";
import { Placeholder as ArticleHeaderAdSlotPlaceholder } from "~/components/article/article-header-ad-slot/styles";
import { DescriptionContainer as ArticleImageDescription } from "~/components/article/article-image/styles";
import { ArticleLeadingVideoContainer } from "~/components/article/article-leading/styles";
import { pageStyle } from "~/components/article/article-render/article-content/magazine-styles";
import { StyleArticleAuthorBlock } from "~/components/article/article-render/style-article/style-article-author-block";
import {
  ContentReadMoreButtonContainer,
  StyledAdSlot,
  StyledArticleLeading,
} from "~/components/article/article-render/styles";
import { StyledTopicLink } from "~/components/article/article-topics/styles";
import { StyledInlineAdSlot } from "~/components/content/content-schema-render/inline-ad-slot/styles";
import { StyledSCMPYouTubeVideoFooter } from "~/components/scmp-youtube-video/styles";
import { setFullContentWidthStyles, setFullViewportWidthStyles } from "~/lib/styles";

export const StyleArticleContainer = styled.div`
  display: grid;
  grid:
    "section" min-content
    "headline" min-content
    "author" min-content
    "leading" min-content
    "topic-social-container" min-content
    "sub-headline" min-content
    "mobile-before-content-header-ad" min-content
    "content" min-content
    "topic" min-content
    "author-detail" min-content
    "more-on-this" min-content
    / 100%;

  inline-size: 100%;

  ${props => props.theme.breakpoints.up("desktop")} {
    grid:
      "header-ad" min-content
      "section" min-content
      "headline" min-content
      "author" min-content
      "leading" min-content
      "topic-social-container" min-content
      "sub-headline" min-content
      "desktop-before-content-header-ad" min-content
      "content" min-content
      "topic" min-content
      "author-detail" min-content
      "more-on-this" min-content
      / 100%;
  }

  > *:empty {
    margin: 0;
    padding: 0;
  }

  *::selection {
    background: #e7e7e7;
  }
`;

export const DesktopHeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: header-ad;

  display: none;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: block;
  }

  ${ArticleHeaderAdSlotPlaceholder} {
    padding: 0;
  }

  ${setFullContentWidthStyles()}
`;

export const MobileUpBeforeContentAdSlot = styled.div`
  grid-area: mobile-before-content-header-ad;

  margin-block-start: 40px;
  margin-block-end: -8px;

  ${setFullViewportWidthStyles()}

  ${props => props.theme.breakpoints.up("desktop")} {
    display: none;
  }

  ${StyledAdSlot} {
    inline-size: fit-content;
    margin: auto;
  }
`;

export const DesktopBeforeContentAdSlot = styled.div`
  grid-area: desktop-before-content-header-ad;

  display: none;

  margin-block-start: 48px;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: block;
  }

  ${StyledAdSlot} {
    inline-size: fit-content;
    margin: auto;
  }
`;

export const Section = styled.div`
  grid-area: section;

  margin-block-start: 32px;

  text-align: center;

  ${props => props.theme.breakpoints.only("tablet")} {
    margin-block-start: 56px;
  }
`;

export const Headline = styled.div`
  grid-area: headline;

  margin-block-start: 16px;
  margin-inline: auto;

  text-align: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 582px;
    margin-block-start: 24px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 824px;
  }

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    max-inline-size: 972px;
  }

  ${props => props.theme.breakpoints.up("largeDesktop")} {
    max-inline-size: 1092px;
  }
`;

export const Author = styled(StyleArticleAuthorBlock)`
  grid-area: author;

  margin-block-start: 40px;

  text-align: center;

  ${props => props.theme.breakpoints.up("desktop")} {
    margin-block-start: 48px;
  }
`;

export const Leading = styled.div`
  grid-area: leading;

  margin-block-start: 40px;
  margin-inline: auto;

  ${setFullViewportWidthStyles()}

  ${ArticleImageDescription} {
    margin-block-start: 8px;
    padding-inline: 20px;

    color: #777777;
    font-weight: 400;
    font-size: 14px;
    font-family: ${fontPlayfairDisplay};
    font-style: italic;
    line-height: 22px;

    ${props => props.theme.breakpoints.up("tablet")} {
      margin-block-start: 16px;
      padding-inline: 40px;
    }

    ${props => props.theme.breakpoints.up("xLargeDesktop")} {
      max-inline-size: calc(100vw - 80px);
      margin-inline: auto;
      padding-inline: 0;
    }
  }

  ${StyledArticleLeading} {
    max-inline-size: 1676px;
    margin-inline: auto;
  }

  ${ArticleLeadingVideoContainer} {
    block-size: auto;
    max-inline-size: 1184px;
  }

  ${StyledSCMPYouTubeVideoFooter} {
    display: none;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 48px;
  }
`;

export const Follow = styled.div`
  flex: 1 1;

  :empty {
    display: none;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 340px;
  }
`;

export const Social = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;

  button:hover {
    opacity: 0.425;
  }

  ${props => props.theme.breakpoints.down("tablet")} {
    display: none;
  }
`;

export const ShareWidget = styled.div``;

export const ActionWidget = styled.div``;

export const TopicSocialContainer = styled.div`
  grid-area: topic-social-container;

  display: flex;
  flex-direction: row;
  gap: 22px;

  inline-size: 100%;
  max-inline-size: 582px;
  margin-block-start: 40px;
  margin-inline: auto;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 80px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    justify-content: space-between;

    max-inline-size: 660px;
  }
`;

export const SubHeadline = styled.div`
  grid-area: sub-headline;

  max-inline-size: 582px;
  margin-block-start: 40px;
  margin-inline: auto;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 48px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 660px;
  }
`;

export const Content = styled.div`
  grid-area: content;

  max-inline-size: 100%;
  margin-inline: auto;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 582px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 660px;
  }

  ${props => pageStyle(props)}

  ${StyledInlineAdSlot} {
    background-color: #ffffff;
  }

  ${ContentReadMoreButtonContainer} {
    margin-block-start: 64px;
  }

  ${ArticleImageDescription} {
    padding-inline: 0;
  }
`;

export const Topic = styled.div`
  grid-area: topic;

  inline-size: 100%;
  max-inline-size: 100%;
  margin-block-start: 40px;

  line-height: normal;

  ${props => props.theme.breakpoints.up("tablet")} {
    justify-self: center;

    max-inline-size: 582px;
    margin-block-start: 64px;
    margin-inline: 0;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 660px;
  }

  ${StyledTopicLink} {
    letter-spacing: 0.8px !important;

    ${props => props.theme.breakpoints.up("tablet")} {
      letter-spacing: 1.6px !important;
    }
  }
`;

export const AuthorDetail = styled.div`
  grid-area: author-detail;

  justify-self: center;

  inline-size: 100%;
  max-inline-size: 100%;
  margin-block-start: 40px;

  ${StyleAuthorCardContainer} {
    inline-size: 100%;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 582px;
    margin-block-start: 80px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 660px;
  }
`;

export const MoreOnThis = styled.div`
  grid-area: more-on-this;

  margin-block-start: 64px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 120px;
  }
`;
